import { Reducer } from 'react';

import {
    CustomerTypes,
    PlayType,
    PlayTypeNameEnum,
    PlayTypes,
    ProgramTypesList,
    PrsDeductionCapTypeEnum,
    RatchetDefinition,
    RatchetDefinitions,
    RatchetModel,
    RatchetModelsEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { partition } from 'lodash';
import { ServiceId } from 'src/constants';
import {
    IPrimeAMFRateCardValuesAction,
    IPrimeAMFResetCustomerTypeAction,
    IPrimeAMFSetAdRevShareAction,
    IPrimeAMFSetConversionModelAction,
    IPrimeAMFSetCppGridRowDataAction,
    IPrimeAMFSetCustomerTypeAction,
    IPrimeAMFSetHouseholdSharingTerritoriesAction,
    IPrimeAMFSetMauGridRowDataAction,
    IPrimeAMFSetOnDemandDefinitionAction,
    IPrimeAMFSetPlayTypeAction,
    IPrimeAMFSetProgramTypeAction,
    IPrimeAMFSetPrsDeductionCapAction,
    IPrimeAMFSetUmgGuardrailTerritoriesAction
} from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues } from 'src/types/IRateCard';

export const rateCardValuesReducer: Reducer<IPrimeAMFRateCardValues, IPrimeAMFRateCardValuesAction> = (
    rateCardValues,
    action
): IPrimeAMFRateCardValues => {
    switch (action.type) {
        // Entire rate card state
        case 'set_rate_card_values':
            return action.rateCardValues!;

        // Cost per play
        case 'reset_cost_per_play':
            return resetCostPerPlay(rateCardValues);

        // MAU
        case 'reset_monthly_active_user':
            return resetMonthlyActiveUser(rateCardValues);

        // Play types
        case 'set_play_type':
            return setPlayTypes(rateCardValues, action);
        case 'set_on_demand_definition':
            return setOnDemandDefinition(rateCardValues, action);
        case 'reset_play_types':
            return resetPlayTypeSection(rateCardValues);

        // Customer types
        case 'set_customer_type':
            return setCustomerType(rateCardValues, action);
        case 'reset_customer_types':
            return resetCustomerTypeSection(rateCardValues, action);

        // Program type
        case 'set_program_type':
            return setProgramType(rateCardValues, action);
        case 'reset_program_types':
            return resetProgramTypeSection(rateCardValues);
        case 'set_household_sharing_territories':
            return setHouseholdSharingTerritories(rateCardValues, action);

        // Conversion
        case 'set_conversion_model':
            return setConversionModel(rateCardValues, action);
        case 'set_umg_guardrail':
            return setUmgGuardrailTerritories(rateCardValues, action);

        // PRS deduction cap
        case 'set_prs_deduction_cap':
            return setPrsDeductionCap(rateCardValues, action);

        // Ad rev share
        case 'reset_ad_rev_share':
            return resetAdRevShare(rateCardValues);
        case 'set_ad_rev_share_percentage':
            return setAdRevSharePercentage(rateCardValues, action as IPrimeAMFSetAdRevShareAction);
        case 'set_ad_rev_share_territories':
            return setAdRevShareTerritories(rateCardValues, action as any);

        // Core rate grid row data
        case 'set_cpp_grid_row_data':
            return setCppGridRowData(rateCardValues, action);
        case 'set_mau_grid_row_data':
            return setMauGridRowData(rateCardValues, action);

        // Invalid action
        default:
            throw Error(`Unknown action: ${action}`);
    }
};

const resetCostPerPlay = (prevRateCardValues: IPrimeAMFRateCardValues): IPrimeAMFRateCardValues => ({
    ...prevRateCardValues,
    primeContractTerms: {
        ...prevRateCardValues.primeContractTerms,
        costPerPlay: {
            playTypes: [],
            programTypeList: [],
            customerTypes: [],
            conversions: undefined,
            householdSharingTerritoryIds: new Set()
        },
        prsDeductionCap: {
            type: PrsDeductionCapTypeEnum.ROYALTY,
            value: '1'
        }
    }
});

const resetMonthlyActiveUser = (prevRateCardValues: IPrimeAMFRateCardValues): IPrimeAMFRateCardValues => ({
    ...prevRateCardValues,
    primeContractTerms: {
        ...prevRateCardValues.primeContractTerms,
        monthlyActiveUser: {
            customerType: []
        }
    }
});

const setPlayTypes = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetPlayTypeAction
): IPrimeAMFRateCardValues => {
    const isPrime = prevRateCardValues.serviceId === ServiceId.Prime;
    let newPlayTypes: PlayTypes =
        (isPrime
            ? prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes
            : prevRateCardValues.amfContractTerms?.adRevShare?.playTypes) ?? [];

    // Add
    if (action.shouldBeIncluded) {
        // If ON_DEMAND, default playlist selection to first definition
        const playTypeToAdd: PlayType =
            action.playType.playTypeName === PlayTypeNameEnum.ON_DEMAND
                ? {
                      ...action.playType,
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      playTypeDefinitions: [action.playType.playTypeDefinitions![0]]
                  }
                : action.playType;

        newPlayTypes = newPlayTypes?.concat([playTypeToAdd]);
    }

    // Remove
    else {
        newPlayTypes = newPlayTypes?.filter((playType) => playType.playTypeId !== action.playType.playTypeId);
    }

    const newRateCardValues: IPrimeAMFRateCardValues = isPrime
        ? {
              ...prevRateCardValues,
              primeContractTerms: {
                  ...prevRateCardValues.primeContractTerms,
                  costPerPlay: {
                      ...prevRateCardValues.primeContractTerms?.costPerPlay,
                      programTypeList: prevRateCardValues.primeContractTerms?.costPerPlay?.programTypeList ?? [],
                      playTypes: newPlayTypes
                  }
              }
          }
        : {
              ...prevRateCardValues,
              amfContractTerms: {
                  ...prevRateCardValues.amfContractTerms,
                  adRevShare: {
                      ...prevRateCardValues.amfContractTerms?.adRevShare,
                      revSharePercent: prevRateCardValues.amfContractTerms?.adRevShare.revSharePercent ?? '',
                      playTypes: newPlayTypes
                  }
              }
          };

    return newRateCardValues;
};

const setOnDemandDefinition = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetOnDemandDefinitionAction
) => {
    const isPrime = prevRateCardValues.serviceId === ServiceId.Prime;
    const newPlayTypes: PlayTypes =
        (isPrime
            ? prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes
            : prevRateCardValues.amfContractTerms?.adRevShare?.playTypes) ?? [];
    const [onDemandPlayType, otherPlayTypes] = partition(
        newPlayTypes,
        (playType) => playType.playTypeName === PlayTypeNameEnum.ON_DEMAND
    );

    const newOnDemandPlayType: PlayType = {
        ...onDemandPlayType[0],
        playTypeDefinitions: [action.onDemandDefinition]
    };

    const newRateCardValues: IPrimeAMFRateCardValues = isPrime
        ? {
              ...prevRateCardValues,
              primeContractTerms: {
                  ...prevRateCardValues.primeContractTerms,
                  costPerPlay: {
                      ...prevRateCardValues.primeContractTerms?.costPerPlay,
                      programTypeList: prevRateCardValues.primeContractTerms?.costPerPlay?.programTypeList ?? [],
                      playTypes: otherPlayTypes.concat([newOnDemandPlayType])
                  }
              }
          }
        : {
              ...prevRateCardValues,
              amfContractTerms: {
                  ...prevRateCardValues.amfContractTerms,
                  adRevShare: {
                      ...prevRateCardValues.amfContractTerms?.adRevShare,
                      revSharePercent: prevRateCardValues.amfContractTerms?.adRevShare.revSharePercent ?? '',
                      playTypes: otherPlayTypes.concat([newOnDemandPlayType])
                  }
              }
          };

    return newRateCardValues;
};

const resetPlayTypeSection = (prevRateCardValues: IPrimeAMFRateCardValues): IPrimeAMFRateCardValues => {
    const isPrime = prevRateCardValues.serviceId === ServiceId.Prime;
    const newRateCardValues: IPrimeAMFRateCardValues = isPrime
        ? {
              ...prevRateCardValues,
              primeContractTerms: {
                  ...prevRateCardValues.primeContractTerms,
                  costPerPlay: {
                      ...prevRateCardValues.primeContractTerms?.costPerPlay,
                      programTypeList: prevRateCardValues.primeContractTerms?.costPerPlay?.programTypeList ?? [],
                      playTypes: []
                  }
              }
          }
        : {
              ...prevRateCardValues,
              amfContractTerms: {
                  ...prevRateCardValues.amfContractTerms,
                  adRevShare: {
                      ...prevRateCardValues.amfContractTerms?.adRevShare,
                      revSharePercent: prevRateCardValues.amfContractTerms?.adRevShare.revSharePercent ?? '',
                      playTypes: []
                  }
              }
          };

    return newRateCardValues;
};

const setCustomerType = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetCustomerTypeAction
): IPrimeAMFRateCardValues => {
    let newCustomerTypes: CustomerTypes = action.isMAU
        ? (prevRateCardValues.primeContractTerms?.monthlyActiveUser?.customerType ?? [])
        : (prevRateCardValues.primeContractTerms?.costPerPlay?.customerTypes ?? []);

    // Add
    if (action.shouldBeIncluded) {
        newCustomerTypes = newCustomerTypes.concat([action.customerType]);
    }

    // Remove
    else {
        newCustomerTypes = newCustomerTypes.filter((customerType) => customerType !== action.customerType);
    }

    const newRateCardValues: IPrimeAMFRateCardValues = action.isMAU
        ? {
              ...prevRateCardValues,
              primeContractTerms: {
                  ...prevRateCardValues.primeContractTerms,
                  monthlyActiveUser: {
                      ...prevRateCardValues.primeContractTerms?.monthlyActiveUser,
                      customerType: newCustomerTypes
                  }
              }
          }
        : {
              ...prevRateCardValues,
              primeContractTerms: {
                  ...prevRateCardValues.primeContractTerms,
                  costPerPlay: {
                      ...prevRateCardValues.primeContractTerms?.costPerPlay,
                      playTypes: prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes ?? [],
                      programTypeList: prevRateCardValues.primeContractTerms?.costPerPlay?.programTypeList ?? [],
                      customerTypes: newCustomerTypes
                  }
              }
          };

    return newRateCardValues;
};

const resetCustomerTypeSection = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFResetCustomerTypeAction
): IPrimeAMFRateCardValues => {
    let newRateCardValues: IPrimeAMFRateCardValues;

    if (action.isMAU) {
        newRateCardValues = {
            ...prevRateCardValues,
            primeContractTerms: {
                ...prevRateCardValues.primeContractTerms,
                monthlyActiveUser: {
                    ...prevRateCardValues.primeContractTerms?.monthlyActiveUser,
                    customerType: []
                }
            }
        };
    } else {
        newRateCardValues = {
            ...prevRateCardValues,
            primeContractTerms: {
                ...prevRateCardValues.primeContractTerms,
                costPerPlay: {
                    ...prevRateCardValues.primeContractTerms?.costPerPlay,
                    playTypes: prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes ?? [],
                    programTypeList: prevRateCardValues.primeContractTerms?.costPerPlay?.programTypeList ?? [],
                    customerTypes: []
                }
            }
        };
    }

    return newRateCardValues;
};

const setProgramType = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetProgramTypeAction
): IPrimeAMFRateCardValues => {
    let newProgramTypes: ProgramTypesList = prevRateCardValues.primeContractTerms?.costPerPlay?.programTypeList ?? [];

    // Add
    if (action.shouldBeIncluded) {
        newProgramTypes = newProgramTypes
            .filter((pt) => pt.programTypeName !== action.programType.programTypeName)
            .concat([action.programType]);
    }

    // Remove
    else {
        newProgramTypes = newProgramTypes.filter(
            (programType) => programType.programTypeName !== action.programType.programTypeName
        );
    }

    const newRateCardValues: IPrimeAMFRateCardValues = {
        ...prevRateCardValues,
        primeContractTerms: {
            ...prevRateCardValues.primeContractTerms,
            costPerPlay: {
                ...prevRateCardValues.primeContractTerms?.costPerPlay,
                playTypes: prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes ?? [],
                programTypeList: newProgramTypes
            }
        }
    };

    return newRateCardValues;
};

const resetProgramTypeSection = (prevRateCardValues: IPrimeAMFRateCardValues): IPrimeAMFRateCardValues => ({
    ...prevRateCardValues,
    primeContractTerms: {
        ...prevRateCardValues.primeContractTerms,
        costPerPlay: {
            ...prevRateCardValues.primeContractTerms?.costPerPlay,
            playTypes: prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes ?? [],
            programTypeList: []
        }
    }
});

const setHouseholdSharingTerritories = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetHouseholdSharingTerritoriesAction
): IPrimeAMFRateCardValues => ({
    ...prevRateCardValues,
    primeContractTerms: {
        ...prevRateCardValues.primeContractTerms,
        costPerPlay: {
            ...prevRateCardValues.primeContractTerms?.costPerPlay,
            playTypes: prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes ?? [],
            programTypeList: prevRateCardValues.primeContractTerms?.costPerPlay?.programTypeList ?? [],
            householdSharingTerritoryIds: action.householdSharingTerritoryIds
        }
    }
});

const setConversionModel = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetConversionModelAction
): IPrimeAMFRateCardValues => {
    const isPrime = prevRateCardValues.serviceId === ServiceId.Prime;

    const conversionModelToAdd = action.conversionModels;

    const newRateCardValues: IPrimeAMFRateCardValues = isPrime
        ? {
              ...prevRateCardValues,
              primeContractTerms: {
                  ...prevRateCardValues.primeContractTerms,
                  costPerPlay: {
                      ...prevRateCardValues.primeContractTerms?.costPerPlay,
                      playTypes: prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes ?? [],
                      programTypeList: prevRateCardValues.primeContractTerms?.costPerPlay?.programTypeList ?? [],
                      conversions: conversionModelToAdd
                  }
              }
          }
        : {
              ...prevRateCardValues,
              amfContractTerms: {
                  ...prevRateCardValues.amfContractTerms,
                  adRevShare: {
                      revSharePercent: prevRateCardValues.amfContractTerms?.adRevShare.revSharePercent ?? '',
                      playTypes: prevRateCardValues.amfContractTerms?.adRevShare?.playTypes ?? [],
                      conversions: conversionModelToAdd
                  }
              }
          };

    return newRateCardValues;
};

const setUmgGuardrailTerritories = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetUmgGuardrailTerritoriesAction
): IPrimeAMFRateCardValues => {
    const [prevUmgGuardrailConversionModel, restOfConversionModels] = partition(
        prevRateCardValues.primeContractTerms?.costPerPlay?.conversions,
        (ratchetModel) => ratchetModel.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_FLOOR_UMG_GUARDRAIL
    );

    const newUmgGuardrailDefinitions: RatchetDefinitions =
        prevUmgGuardrailConversionModel[0].ratchetDefinitions?.map((definition) => ({
            ...definition,
            value: action.shouldBeToggled.toString()
        })) ?? [];

    const newUmgGuardrailConversionModel: RatchetModel = {
        ...prevUmgGuardrailConversionModel[0],
        ratchetDefinitions: newUmgGuardrailDefinitions
    };

    return {
        ...prevRateCardValues,
        primeContractTerms: {
            ...prevRateCardValues.primeContractTerms,
            costPerPlay: {
                ...prevRateCardValues.primeContractTerms?.costPerPlay,
                playTypes: prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes ?? [],
                programTypeList: prevRateCardValues.primeContractTerms?.costPerPlay?.programTypeList ?? [],
                conversions: [...restOfConversionModels, newUmgGuardrailConversionModel]
            }
        }
    };
};

const setPrsDeductionCap = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetPrsDeductionCapAction
): IPrimeAMFRateCardValues => ({
    ...prevRateCardValues,
    primeContractTerms: {
        ...prevRateCardValues.primeContractTerms,
        prsDeductionCap: {
            value: action.deductionCap,
            type: PrsDeductionCapTypeEnum.ROYALTY
        }
    }
});

const resetAdRevShare = (prevRateCardValues: IPrimeAMFRateCardValues): IPrimeAMFRateCardValues => {
    const isPrime = prevRateCardValues.serviceId === ServiceId.Prime;

    return isPrime
        ? {
              ...prevRateCardValues,
              primeContractTerms: {
                  ...prevRateCardValues.primeContractTerms,
                  adRevShare: {
                      revSharePercent: '',
                      playTypes: [],
                      conversions: undefined,
                      territories: []
                  }
              }
          }
        : {
              ...prevRateCardValues,
              amfContractTerms: {
                  ...prevRateCardValues.amfContractTerms,
                  adRevShare: {
                      revSharePercent: '',
                      playTypes: [],
                      conversions: undefined,
                      territories: []
                  }
              }
          };
};

const setAdRevSharePercentage = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetAdRevShareAction
): IPrimeAMFRateCardValues => {
    const isPrime = prevRateCardValues.serviceId === ServiceId.Prime;

    return isPrime
        ? {
              ...prevRateCardValues,
              primeContractTerms: {
                  ...prevRateCardValues.primeContractTerms,
                  adRevShare: {
                      ...prevRateCardValues.primeContractTerms?.adRevShare,
                      playTypes: prevRateCardValues.primeContractTerms?.costPerPlay?.playTypes ?? [],
                      revSharePercent: action.revSharePercent.toString()
                  }
              }
          }
        : {
              ...prevRateCardValues,
              amfContractTerms: {
                  ...prevRateCardValues.amfContractTerms,
                  adRevShare: {
                      ...prevRateCardValues.amfContractTerms?.adRevShare,
                      playTypes: prevRateCardValues.amfContractTerms?.adRevShare?.playTypes ?? [],
                      revSharePercent: action.revSharePercent.toString()
                  }
              }
          };
};

const setCppGridRowData = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetCppGridRowDataAction
): IPrimeAMFRateCardValues => ({
    ...prevRateCardValues,
    rateCardRowData: {
        ...prevRateCardValues.rateCardRowData,
        cpp: action.rowData
    }
});

const setMauGridRowData = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: IPrimeAMFSetMauGridRowDataAction
): IPrimeAMFRateCardValues => ({
    ...prevRateCardValues,
    rateCardRowData: {
        ...prevRateCardValues.rateCardRowData,
        mau: action.rowData
    }
});

const setAdRevShareTerritories = (
    prevRateCardValues: IPrimeAMFRateCardValues,
    action: { territories: string[] }
): IPrimeAMFRateCardValues => ({
    ...prevRateCardValues,
    primeContractTerms: {
        ...prevRateCardValues.primeContractTerms,
        adRevShare: {
            ...prevRateCardValues.primeContractTerms?.adRevShare,
            playTypes: prevRateCardValues.primeContractTerms?.adRevShare?.playTypes ?? [],
            revSharePercent: prevRateCardValues.primeContractTerms?.adRevShare?.revSharePercent ?? '',
            territories: action.territories
        }
    }
});
